/**
 * The two columns a list view is made of.
 *
 * A member, and the lists it belongs to. Both are usually link rows, and a
 * link row may be shown by a column of the *linked* table rather than by its
 * primary one -- so each of the two is a pair of choices, and the second only
 * means anything once the first names a link.
 *
 * Changing either column sends its target as null with it. The backend clears
 * a target whose link has changed -- it would name a column of a table the new
 * link does not point at -- and saying so in the request is what keeps the
 * cached view from claiming a target the server has already dropped.
 */
import { Rows3 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { Field, ViewOf } from '@/api/types'
import { Button } from '@/shared/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu'
import { linkedTableId } from '../fields/relations'
import { listFields, targetFields } from '../views/column-choices'
import { FieldChoice } from './field-choice'
import { useFields } from './queries'
import { useViewSettings } from './view-settings-mutations'

export function ListColumnsMenu({
  tableId,
  view,
  fields,
}: {
  tableId: number
  view: ViewOf<'list'>
  fields: readonly Field[]
}) {
  const { t } = useTranslation()
  const { choose } = useViewSettings(tableId, view.id)

  const member = fields.find((field) => field.id === view.member_field) ?? undefined
  const list = fields.find((field) => field.id === view.list_field) ?? undefined
  const lists = listFields(fields)

  // Asked for unconditionally, which is what `enabled` on the query would
  // otherwise be for: the table's own id is one there is no harm in asking
  // about, and its fields are already cached because this menu was drawn.
  const beyondMember = useFields(linkedTableId(member) ?? tableId)
  const beyondList = useFields(linkedTableId(list) ?? tableId)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5">
          <Rows3 className="size-4 opacity-70" />
          {t('view.columns')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-56">
        <FieldChoice
          label={t('view.memberField')}
          chosen={view.member_field ?? null}
          candidates={fields}
          onChoose={(member_field) => {
            choose({ member_field, member_target_field: null })
          }}
        />
        {linkedTableId(member) !== null && (
          <FieldChoice
            label={t('view.memberTargetField')}
            chosen={view.member_target_field ?? null}
            candidates={targetFields(beyondMember.data ?? [])}
            noneLabel={t('view.targetPrimary')}
            onChoose={(member_target_field) => {
              choose({ member_target_field })
            }}
          />
        )}

        <DropdownMenuSeparator />

        {lists.length === 0 ? (
          <DropdownMenuItem disabled>{t('view.listNoFields')}</DropdownMenuItem>
        ) : (
          <FieldChoice
            label={t('view.listField')}
            chosen={view.list_field ?? null}
            candidates={lists}
            onChoose={(list_field) => {
              // The allowed values name rows or options of the column that was
              // there, so they mean nothing once another column supplies the
              // lists. The backend resets both; saying it here keeps the
              // cached view from filtering by the old field's ids meanwhile.
              choose({
                list_field,
                list_target_field: null,
                include_all_list_values: true,
                allowed_list_values: [],
              })
            }}
          />
        )}
        {linkedTableId(list) !== null && (
          <FieldChoice
            label={t('view.listTargetField')}
            chosen={view.list_target_field ?? null}
            candidates={targetFields(beyondList.data ?? [])}
            noneLabel={t('view.targetPrimary')}
            onChoose={(list_target_field) => {
              choose({ list_target_field })
            }}
          />
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
